/**
 * The Inspector: what Hearth knows about one message, and the message itself.
 *
 * `settings.md` §4 says every geek surface lives in one of two containers — the
 * Inspector drawer or the Console — and that "Deep off is therefore a complete
 * removal, not a scattering of leftovers". This is the first of the two, and it
 * is deliberately the reading half: nothing here changes anything.
 *
 * EVERY FIELD IS A ROW THE SYNC PATH ALREADY WROTE. The audit found the
 * Inspector's data "already exists at the origin and is never exposed", and the
 * origin's `/inspect` is that exposure rather than a new store — so this
 * component is a renderer and not a second model.
 *
 * SHOW ORIGINAL IS A DOWNLOAD, NOT A PANE. `/messages/:guid/raw` answers with
 * `Content-Disposition: attachment`, because a browser handed `message/rfc822`
 * inline either renders it as text or offers it to a mail client. A reader who
 * wants the original wants the file.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { api, type MessageRow } from './api.ts'

export interface InspectorProps {
  /** The message to inspect, or null when the drawer is closed. */
  readonly row: MessageRow | null
  readonly onClose: () => void
  readonly onError: (message: string) => void
}

interface Inspected {
  readonly guid: string
  readonly locations: { mailbox: string; uidvalidity: number; uid: number; flags: string | null }[]
  readonly keywords: string[]
  readonly attachments: { part: string; filename: string | null; type: string; size: number }[]
  readonly threading: { messageId: string | null; threadId: string | null }
  readonly ref: string | null
  readonly dates: { header: number }
  readonly size: number
}

export function Inspector({ row, onClose, onError }: InspectorProps): React.JSX.Element | null {
  const ids = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)
  const [data, setData] = useState<Inspected | null>(null)

  useEffect(() => {
    setData(null)
    if (!row) return
    let live = true
    api
      .inspect(row.id)
      .then((result) => {
        if (live) setData(result as Inspected)
      })
      .catch((error: unknown) => onError(error instanceof Error ? error.message : 'Could not inspect that message'))
    return () => {
      live = false
    }
  }, [row, onError])

  useEffect(() => {
    if (!row) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [row, onClose])

  useEffect(() => {
    if (!row) return
    const panel = panelRef.current
    const opener = document.activeElement
    if (opener instanceof HTMLElement && !panel?.contains(opener)) returnTo.current = opener
    panel?.focus()
    return () => returnTo.current?.focus()
  }, [row])

  if (!row) return null

  return (
    <div
      className="settings-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="settings-panel inspector-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="settings-head">
          <h2 className="settings-title" id={`${ids}-title`}>
            Inspector
          </h2>
          <button className="settings-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <dl className="inspector-facts">
          <dt>Subject</dt>
          <dd>{row.subject ?? <em>none</em>}</dd>

          <dt>Identity</dt>
          {/* Dovecot's X-GUID, which is what every table in the index keys on.
              Shown because "which message is this, exactly" is the question the
              rest of the drawer is answering about. */}
          <dd className="inspector-mono">{row.id}</dd>

          <dt>Message-ID</dt>
          <dd className="inspector-mono">{data?.threading.messageId ?? '—'}</dd>

          <dt>Thread</dt>
          <dd className="inspector-mono">{data?.threading.threadId ?? '—'}</dd>

          <dt>In</dt>
          <dd>
            {data ? (
              <ul className="inspector-locations">
                {/* ONE MESSAGE CAN BE IN SEVERAL MAILBOXES, and that is the
                    first thing anybody asks when a filing rule surprises them. */}
                {data.locations.map((location) => (
                  <li key={`${location.mailbox}:${location.uid}`}>
                    <span>{location.mailbox}</span>
                    <span className="inspector-mono">uid {location.uid}</span>
                    <span className="inspector-flags">{(location.flags ?? '').trim() || 'no flags'}</span>
                  </li>
                ))}
              </ul>
            ) : (
              '…'
            )}
          </dd>

          {data && data.keywords.length > 0 && (
            <>
              <dt>Labels</dt>
              <dd>{data.keywords.join(', ')}</dd>
            </>
          )}

          {data && data.attachments.length > 0 && (
            <>
              <dt>Parts</dt>
              <dd>
                <ul className="inspector-locations">
                  {data.attachments.map((part) => (
                    <li key={part.part}>
                      <span className="inspector-mono">{part.part}</span>
                      <span>{part.filename ?? part.type}</span>
                      <span className="inspector-flags">{Math.max(1, Math.round(part.size / 1000))} KB</span>
                    </li>
                  ))}
                </ul>
              </dd>
            </>
          )}

          <dt>Permalink</dt>
          {/* AN IDENTIFIER, NOT A CAPABILITY — decisions.md settles that, and
              saying so here is the point: people assume the opposite of any
              long random-looking string, and act on that assumption. */}
          <dd>
            {data?.ref ? (
              <>
                <span className="inspector-mono">/m/{data.ref}</span>
                <span className="inspector-note">
                  A name for this message, not a key to it — opening it still needs your account.
                </span>
              </>
            ) : (
              '—'
            )}
          </dd>
        </dl>

        <div className="compose-actions">
          <a className="compose-cancel" href={api.rawUrl(row.id)} download>
            Show original (.eml)
          </a>
        </div>
      </div>
    </div>
  )
}
